<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import type { ProductFilterTag } from '~/types/api'
import type { ProductFilterTagCollectionCategoryWithCollections } from '~/types/productFilterTags'

const breakpoints = useBreakpoints(breakpointsTailwind)
const isSmall = breakpoints.smallerOrEqual('sm')

const selectedFilterTagsIds = defineModel<number[]>({
  required: true,
})

const props = withDefaults(
  defineProps<{
    smartFilterTags?: ProductFilterTag[]
    filterTags?: ProductFilterTagCollectionCategoryWithCollections[]
    prefix?: string
  }>(),
  {
    smartFilterTags: () => [],
    filterTags: () => [],
  }
)

const activeCategory =
  ref<ProductFilterTagCollectionCategoryWithCollections | null>(null)

function isSelected(tagNid: number) {
  return !!selectedFilterTagsIds.value.find((tag) => tag === tagNid)
}

function isSmartTag(tagNid: number) {
  return !!props.smartFilterTags.find((tag) => tag.nid === tagNid)
}

const CATEGORY_CONTAINER = props.prefix
  ? props.prefix + 'categoryContainer'
  : 'categoryContainer'
const MAIN_CONTAINER = props.prefix
  ? props.prefix + 'mainContainer'
  : 'mainContainer'

function onEnter(el: HTMLElement, done: () => void) {
  el.style.height = '0'
  el.offsetHeight // Trigger a reflow, flushing the CSS changes
  el.style.height = el.scrollHeight + 'px'

  el.addEventListener('transitionend', done, { once: true })
}

function onBeforeLeave(el: HTMLElement) {
  el.style.height = el.scrollHeight + 'px'
  el.offsetHeight // Trigger a reflow, flushing the CSS changes
}

function onAfterEnter(el: HTMLElement) {
  el.style.height = 'auto'
}

function onLeave(el: HTMLElement, done: () => void) {
  el.style.height = '0'

  el.addEventListener('transitionend', done, { once: true })
}
</script>

<template>
  <div class="w-full @container/main">
    <div class="flex flex-col flex-wrap items-start gap-2 sm:flex-row lg:gap-8">
      <template v-for="category in props.filterTags || []" :key="category.nid">
        <button
          class="relative flex items-center gap-2 text-lg font-extrabold after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-black after:transition-all after:duration-500 after:ease-in-out after:content-[''] hover:after:w-full"
          :class="{
            'after:!w-full': activeCategory?.nid === category.nid,
          }"
          @click="
            () => {
              if (activeCategory?.nid === category.nid) {
                activeCategory = null
              } else {
                activeCategory = category
              }
            }
          "
        >
          {{ category?.title }}
          <div>
            <IconDropdownArrow
              :class="{
                'rotate-180 transform': activeCategory?.nid === category.nid,
              }"
            />
          </div>
        </button>
        <div
          v-if="isSmall && activeCategory?.nid === category.nid"
          :id="CATEGORY_CONTAINER + category.nid"
        ></div>
      </template>
    </div>
    <div :id="MAIN_CONTAINER"></div>
    <ClientOnly>
      <Teleport
        :to="
          '#' +
          (activeCategory && isSmall
            ? CATEGORY_CONTAINER + activeCategory?.nid
            : MAIN_CONTAINER)
        "
      >
        <Transition
          enter-active-class="overflow-hidden transition-[height] duration-200 ease-out"
          leave-active-class="overflow-hidden transition-[height] duration-200 ease-out"
          @enter="onEnter"
          @after-enter="onAfterEnter"
          @before-leave="onBeforeLeave"
          @leave="onLeave"
        >
          <div :key="activeCategory.nid" v-if="activeCategory">
            <div
              class="my-5 flex flex-row flex-wrap gap-x-10 gap-y-8 lg:gap-x-16"
            >
              <span v-if="activeCategory.collections.length === 0">
                {{ $t('base.products.filters.noCollections') }}
              </span>
              <div
                v-for="collection in activeCategory.collections"
                :key="collection.nid"
                class="flex flex-col gap-4"
              >
                <span class="font-pragmatica-ext text-sm font-bold">
                  {{ collection.title }}
                </span>
                <div class="grid gap-1 gap-x-5 lg:grid-flow-col lg:grid-rows-5">
                  <span v-if="collection.tags.length === 0" class="ml-4">
                    {{ $t('base.products.filters.noTags') }}
                  </span>
                  <div
                    v-for="tag in collection.tags"
                    :key="tag.nid"
                    class="flex flex-row items-center"
                  >
                    <ProductsFiltersChip
                      :class="{
                        '!px-0': !isSelected(tag.nid) && !isSmartTag(tag.nid),
                      }"
                      :title="tag.displayLabel || tag.title || ''"
                      :removable="isSelected(tag.nid) && !isSmartTag(tag.nid)"
                      :plain="!isSelected(tag.nid) && !isSmartTag(tag.nid)"
                      :clickable="!isSelected(tag.nid) && !isSmartTag(tag.nid)"
                      :disabled="
                        tag.productCount === 0 &&
                        !isSmartTag(tag.nid) &&
                        !isSelected(tag.nid)
                      "
                      :underline="!isSelected(tag.nid) && !isSmartTag(tag.nid)"
                      @click="() => selectedFilterTagsIds.push(tag.nid)"
                      @remove="
                        () =>
                          (selectedFilterTagsIds = selectedFilterTagsIds.filter(
                            (t) => t !== tag.nid
                          ))
                      "
                    />
                    <span
                      v-if="!isSelected(tag.nid) && !isSmartTag(tag.nid)"
                      class="ml-3 mt-[2px] text-xs text-neutral-400"
                    >
                      {{ tag.productSetCount }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </ClientOnly>
  </div>
</template>
